import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import apiClient from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2, ArrowRight, ArrowLeft, CheckCircle2,
  MapPin, Home, DollarSign, Hammer, Calendar, TrendingUp,
  Sparkles, LineChart, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = ['Empresa', 'Proyecto', 'Números', 'Resultado'];

function fmt(n) {
  if (n == null) return '-';
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n);
}

/* ─── Progress ─── */
function Progress({ current }) {
  return (
    <div className="w-full max-w-[420px] mx-auto mb-10">
      <div className="flex items-center">
        {STEPS.map((label, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold transition-all duration-300"
                  style={{
                    background: done ? 'var(--accent-primary)' : active ? 'var(--text-primary)' : 'var(--bg-hover)',
                    color: done || active ? '#fff' : 'var(--text-muted)',
                  }}
                >
                  {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span
                  className="text-[10px] font-medium whitespace-nowrap transition-colors"
                  style={{ color: done || active ? 'var(--text-primary)' : 'var(--text-muted)' }}
                >
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className="flex-1 mx-2 h-px mt-[-18px]" style={{ background: done ? 'var(--accent-primary)' : 'var(--border-default)' }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Field Wrapper ─── */
function Field({ label, icon: Icon, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-medium flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />}
        {label}
      </Label>
      {children}
      {hint && <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>{hint}</p>}
    </div>
  );
}

/* ─── Animated step ─── */
function Step({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="w-full max-w-[420px] mx-auto"
    >
      {children}
    </motion.div>
  );
}

/* ─── Nav Buttons ─── */
function NavButtons({ onBack, onNext, nextLabel, loading, nextTestId }) {
  return (
    <div className="flex gap-3 mt-10">
      {onBack && (
        <Button variant="outline" onClick={onBack} className="h-11 px-5" style={{ borderColor: 'var(--border-default)' }}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
      )}
      <Button
        onClick={onNext}
        disabled={loading}
        className="flex-1 h-11 text-white font-semibold"
        style={{ background: 'var(--accent-primary)' }}
        onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = 'var(--accent-hover)'; }}
        onMouseOut={(e) => { if (!loading) e.currentTarget.style.background = 'var(--accent-primary)'; }}
        data-testid={nextTestId}
      >
        {loading ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{nextLabel}</span> : <>{nextLabel} <ArrowRight className="ml-2 w-4 h-4" /></>}
      </Button>
    </div>
  );
}

/* ─── Main ─── */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(-1); // -1 = welcome
  const [loading, setLoading] = useState(false);

  const [company, setCompany] = useState({ business_name: '', country: 'México', currency: 'MXN', timezone: 'America/Mexico_City' });
  const [project, setProject] = useState({ name: '', location: '', project_type: 'residencial', total_units: 20 });
  const [projectId, setProjectId] = useState(null);
  const [scenario, setScenario] = useState({ avg_price_per_unit: 2500000, hard_cost_per_unit: 900000, construction_months: 18, absorption_rate: 2 });
  const [results, setResults] = useState(null);
  const [scenarioId, setScenarioId] = useState(null);

  const go = (n) => setStep(n);

  const handleCompany = async () => {
    if (!company.business_name.trim()) { toast.error('Ingresa el nombre de tu empresa'); return; }
    setLoading(true);
    try { await apiClient.post('/onboarding/company-setup', company); go(1); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const handleProject = async () => {
    if (!project.name.trim()) { toast.error('Dale un nombre a tu proyecto'); return; }
    setLoading(true);
    try {
      const res = await apiClient.post('/onboarding/quick-project', project);
      setProjectId(res.data.id);
      go(2);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const handleAnalyze = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await apiClient.post('/onboarding/quick-analysis', {
        project_id: projectId,
        avg_price_per_unit: scenario.avg_price_per_unit,
        hard_cost_per_unit: scenario.hard_cost_per_unit,
        construction_months: scenario.construction_months,
        absorption_rate: scenario.absorption_rate,
      });
      setResults(res.data.metrics);
      setScenarioId(res.data.scenario_id);
      localStorage.setItem('onboarding_completed', 'true');
      go(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al analizar'); }
    setLoading(false);
  };

  const goTo = async (path) => {
    if (refreshUser) await refreshUser();
    navigate(path);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg-primary)' }} data-testid="onboarding-page">
      {/* Header */}
      <div className="shrink-0 h-14 flex items-center px-6" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <img src="/assets/stratika-logo.png" alt="Stratika" className="h-8" style={{ objectFit: 'contain', mixBlendMode: 'multiply' }} />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        {step >= 0 && step < 3 && <Progress current={step} />}

        <AnimatePresence mode="wait">
          {/* ── WELCOME ── */}
          {step === -1 && (
            <Step key="welcome">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ background: 'var(--accent-subtle)' }}>
                  <LineChart className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
                </div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold mb-3 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Conoce el potencial financiero de tu proyecto
                </h1>
                <p className="text-sm leading-relaxed mb-10 max-w-xs mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  En 4 pasos vas a obtener la rentabilidad, el capital requerido y los riesgos principales.
                </p>
                <Button
                  onClick={() => go(0)}
                  className="h-12 px-10 text-sm font-semibold text-white rounded-xl"
                  style={{ background: 'var(--accent-primary)' }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'var(--accent-hover)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'var(--accent-primary)'}
                  data-testid="onboarding-start"
                >
                  Comenzar <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-[11px] mt-5" style={{ color: 'var(--text-muted)' }}>Toma menos de 2 minutos</p>
              </div>
            </Step>
          )}

          {/* ── COMPANY ── */}
          {step === 0 && (
            <Step key="company">
              <h2 className="font-display text-xl font-semibold mb-1 text-center" style={{ color: 'var(--text-primary)' }}>¿Cómo se llama tu empresa?</h2>
              <p className="text-sm mb-8 text-center" style={{ color: 'var(--text-muted)' }}>Esto personaliza tu espacio de trabajo</p>
              <div className="space-y-5">
                <Field label="Nombre de la empresa" icon={Building2}>
                  <Input
                    value={company.business_name}
                    onChange={e => setCompany({ ...company, business_name: e.target.value })}
                    placeholder="Ej: Grupo Inmobiliario Atlas"
                    className="h-11"
                    autoFocus
                    data-testid="onboarding-company-name"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="País">
                    <Select value={company.country} onValueChange={v => setCompany({ ...company, country: v })}>
                      <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['México','Colombia','Chile','Perú','Argentina','Estados Unidos'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Moneda">
                    <Select value={company.currency} onValueChange={v => setCompany({ ...company, currency: v })}>
                      <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['MXN','USD','COP','CLP','PEN'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>
              <NavButtons onBack={() => go(-1)} onNext={handleCompany} nextLabel={loading ? 'Guardando...' : 'Siguiente'} loading={loading} nextTestId="onboarding-company-next" />
            </Step>
          )}

          {/* ── PROJECT ── */}
          {step === 1 && (
            <Step key="project">
              <h2 className="font-display text-xl font-semibold mb-1 text-center" style={{ color: 'var(--text-primary)' }}>Describe tu proyecto</h2>
              <p className="text-sm mb-8 text-center" style={{ color: 'var(--text-muted)' }}>¿Qué desarrollo quieres analizar?</p>
              <div className="space-y-5">
                <Field label="Nombre del proyecto" icon={Home} hint="El nombre con el que identificas este desarrollo">
                  <Input value={project.name} onChange={e => setProject({ ...project, name: e.target.value })} placeholder="Ej: Residencial Los Encinos" className="h-11" autoFocus data-testid="onboarding-project-name" />
                </Field>
                <Field label="Ubicación" icon={MapPin}>
                  <Input value={project.location} onChange={e => setProject({ ...project, location: e.target.value })} placeholder="Ej: Guadalajara, Jalisco" className="h-11" data-testid="onboarding-project-location" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Tipo">
                    <Select value={project.project_type} onValueChange={v => setProject({ ...project, project_type: v })}>
                      <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residencial">Residencial</SelectItem>
                        <SelectItem value="residencial_plus">Residencial Plus</SelectItem>
                        <SelectItem value="comercial">Comercial</SelectItem>
                        <SelectItem value="mixto">Mixto</SelectItem>
                        <SelectItem value="industrial">Industrial</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Unidades a vender" hint="Departamentos, casas, locales, etc.">
                    <Input type="number" min={1} value={project.total_units} onChange={e => setProject({ ...project, total_units: parseInt(e.target.value) || 1 })} className="h-11" data-testid="onboarding-units" />
                  </Field>
                </div>
              </div>
              <NavButtons onBack={() => go(0)} onNext={handleProject} nextLabel={loading ? 'Creando...' : 'Siguiente'} loading={loading} nextTestId="onboarding-project-next" />
            </Step>
          )}

          {/* ── SCENARIO ── */}
          {step === 2 && (
            <Step key="scenario">
              <h2 className="font-display text-xl font-semibold mb-1 text-center" style={{ color: 'var(--text-primary)' }}>Los números del proyecto</h2>
              <p className="text-sm mb-8 text-center" style={{ color: 'var(--text-muted)' }}>No necesitas ser exacto. Puedes ajustar después.</p>
              <div className="space-y-5">
                <Field label="¿A cuánto vendes cada unidad?" icon={DollarSign} hint="Precio de lista promedio por departamento o casa">
                  <Input type="number" min={0} value={scenario.avg_price_per_unit} onChange={e => setScenario({ ...scenario, avg_price_per_unit: parseFloat(e.target.value) || 0 })} className="h-11" autoFocus data-testid="onboarding-price" />
                </Field>
                <Field label="¿Cuánto cuesta construir cada unidad?" icon={Hammer} hint="Costo directo de obra por unidad, sin terreno">
                  <Input type="number" min={0} value={scenario.hard_cost_per_unit} onChange={e => setScenario({ ...scenario, hard_cost_per_unit: parseFloat(e.target.value) || 0 })} className="h-11" data-testid="onboarding-cost" />
                </Field>
                <Field label="¿Cuántos meses dura la obra?" icon={Calendar} hint="Desde inicio de construcción hasta entrega">
                  <Input type="number" min={1} max={60} value={scenario.construction_months} onChange={e => setScenario({ ...scenario, construction_months: parseInt(e.target.value) || 1 })} className="h-11" data-testid="onboarding-months" />
                </Field>
                <Field label="¿Cuántas unidades vendes al mes?" icon={TrendingUp} hint="Velocidad de venta estimada. Ej: 1.5 unidades/mes">
                  <Input type="number" min={0.1} step={0.1} value={scenario.absorption_rate} onChange={e => setScenario({ ...scenario, absorption_rate: parseFloat(e.target.value) || 0.1 })} className="h-11" data-testid="onboarding-absorption" />
                </Field>
              </div>
              <div className="flex gap-3 mt-10">
                <Button variant="outline" onClick={() => go(1)} className="h-11 px-5" style={{ borderColor: 'var(--border-default)' }}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Button
                  onClick={handleAnalyze}
                  disabled={loading}
                  className="flex-1 h-11 text-white font-semibold"
                  style={{ background: 'var(--accent-primary)' }}
                  onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = 'var(--accent-hover)'; }}
                  onMouseOut={(e) => { if (!loading) e.currentTarget.style.background = 'var(--accent-primary)'; }}
                  data-testid="onboarding-analyze"
                >
                  {loading ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Calculando...</span> : <><Sparkles className="mr-2 w-4 h-4" /> Ver resultados</>}
                </Button>
              </div>
            </Step>
          )}

          {/* ── RESULTS ── */}
          {step === 3 && results && (
            <Step key="results">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'var(--success-soft)' }}>
                  <CheckCircle2 className="w-7 h-7" style={{ color: 'var(--success)' }} />
                </div>
                <h2 className="font-display text-xl font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>El potencial de {project.name || 'tu proyecto'}</h2>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Resultados calculados con el motor financiero</p>
              </div>

              {/* Primary KPIs */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                <ResultCard
                  label="Rentabilidad"
                  value={`${Number(results.irr_annual || 0).toFixed(1)}%`}
                  sublabel="TIR anual del inversionista"
                  status={results.irr_annual > 15 ? 'positive' : results.irr_annual > 0 ? 'warning' : 'negative'}
                />
                <ResultCard
                  label="Valor generado"
                  value={`$${fmt(results.npv)}`}
                  sublabel="VAN del inversionista"
                  status={results.npv > 0 ? 'positive' : 'negative'}
                />
                <ResultCard
                  label="Capital necesario"
                  value={`$${fmt(results.peak_equity)}`}
                  sublabel="Inversión máxima"
                  status="neutral"
                />
              </div>
              {/* Secondary KPIs */}
              <div className="grid grid-cols-3 gap-3 mb-10">
                <ResultCard label="Retorno" value={`${Number(results.roi || 0).toFixed(0)}%`} sublabel="ROI" status="neutral" small />
                <ResultCard label="Multiplicador" value={`${Number(results.moic || 0).toFixed(2)}x`} sublabel="MOIC" status="neutral" small />
                <ResultCard label="Ganancia" value={`$${fmt(results.total_profit)}`} sublabel="Utilidad neta" status={results.total_profit > 0 ? 'positive' : 'negative'} small />
              </div>

              <Button
                onClick={() => goTo(projectId && scenarioId ? `/analyzer/wizard/${projectId}/${scenarioId}` : '/analyzer')}
                className="h-11 text-white font-semibold w-full rounded-xl mb-2.5"
                style={{ background: 'var(--accent-primary)' }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--accent-hover)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'var(--accent-primary)'}
                data-testid="onboarding-go-analyzer"
              >
                Refinar mi modelo <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => goTo('/')} className="h-11 w-full rounded-xl" style={{ borderColor: 'var(--border-default)' }} data-testid="onboarding-go-dashboard">
                Ir al dashboard
              </Button>
            </Step>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ─── Result Card ─── */
function ResultCard({ label, value, sublabel, status, small }) {
  const color = { positive: 'var(--success)', warning: 'var(--warning-color)', negative: 'var(--danger)', neutral: 'var(--text-primary)' }[status] || 'var(--text-primary)';
  return (
    <div className={`rounded-xl text-center ${small ? 'p-3' : 'p-4'}`} style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className={`font-display font-bold tabular-nums ${small ? 'text-sm' : 'text-lg'}`} style={{ color }}>{value}</div>
      {sublabel && <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{sublabel}</div>}
    </div>
  );
}
